import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import SectionTitle from '../../shared/sectionTitle';
import UserAvatar from '../../shared/userAvatar';
import CustomSwitch from '../../shared/customSwitch';
import NotificationCard from '../../shared/notificationCard';
import RichTextPreview from '../../shared/richTextPreview';
import Tag from '../../shared/tag';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import DotsMenu from '../../shared/dotsMenu';
import CustomFormDrawer from '../../shared/customFormDrawer';
import PageContainer from '../../shared/pageContainer';
import CustomButton from '../../shared/customButton';
import { APP_PAGES } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { api_job, api_job_public } from '../../../constants/apiRoutes';
import { jobDetailsSelector } from '../../../store/selectors/jobDetailsSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { clearJob, getJob } from '../../../store/modules/jobDetails';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import { useTranslations } from '../../../utility/useTranslations';
import { formatDate } from '../../../utility/dateUtils';
import {
  getObjectToNumberArray,
  isArray,
  isArrayEmpty,
} from '../../../utility/helpers';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import http, { API_DOMAIN, API_PROTOCOL } from '../../../utility/http';
import { getSubdomain, showSuccessMessage } from '../../../utility/uiUtils';
import { getFields, prepareInitialData } from '../jobsPage/config';
import {
  DATE_FORMAT,
  getMenuItems,
  sumCandidates,
  WORKSPACE_MAP,
} from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      marginTop: spacing(8),
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      height: 30,
      margin: spacing(8, 0, 6, 0),
    },
    subtitle: {
      margin: spacing(8, 0, 4, 0),
    },
    notes: {
      position: 'relative',
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      padding: spacing(8),
      minHeight: 80,
      marginBottom: spacing(6),
    },
    summaryRoot: {
      boxSizing: 'border-box',
      position: 'relative',
      backgroundColor: primary.bluish9,
      display: 'grid',
      gridTemplateColumns: `minmax(0, 1fr) minmax(0, 260px)`,
      width: '100%',
      borderRadius: 4,
      marginBottom: spacing(7),
    },
    backButton: {
      position: 'absolute',
      top: 24,
      right: 24,
      zIndex: 1,
    },
    summaryContent: {
      boxSizing: 'border-box',
      padding: spacing(6),
      width: '100%',
    },
    summaryFieldsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      columnGap: spacing(6),
      rowGap: spacing(2),
    },
    summarySide: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      padding: spacing(6, 6, 6, 4),
      borderLeft: `1px dashed ${primary.bluish7}`,
    },
    sectionWrapper: {
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    publicLinkPreview: {
      display: 'block',
      marginBottom: spacing(3),
    },
    publicLink: {
      fontFamily: 'ProximaNova-Bold',
    },
    publishingSubtitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    switch: {
      width: 'fit-content',
      '& > div': {
        gap: 8,
      },
    },
    label: {
      color: primary.bluish3,
    },
    labelSpacing: {
      marginBottom: spacing(1.5),
    },
    headerWrapper: {
      maxWidth: 'calc(100% - 260px)',
    },
    tagsWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: spacing(2),
    },
    tagWrapper: {
      margin: spacing(0, 2, 2, 0),
    },
    metricValue: {
      fontFamily: 'ProximaNova-Bold',
    },
    publicButton: {
      maxHeight: 22,
      padding: spacing(1, 3),
      fontSize: 12,
      lineHeight: '14px',
      fontFamily: 'ProximaNova-Bold',
    },
    cardSubtitle: {
      marginBottom: spacing(4),
    },
    linkButtonWrapper: {
      paddingTop: spacing(2),
      display: 'flex',
      gap: 12,
    },
    editWrapper: {
      position: 'absolute',
      right: 16,
      top: 16,
      height: 20,
      width: 'auto',
      [breakpoints.up('xLg')]: {
        right: 24,
        top: 24,
      },
    },
    dotsMenu: {
      flexShrink: 0,
    },
    customLabelClass: {
      fontSize: 16,
      lineHeight: '24px',
      fontFamily: 'ProximaNova-Bold',
    },
    customLabelWrapperClass: {
      display: 'grid',
      marginLeft: spacing(2),
    },
    customOptionLabelWrapperClass: {
      '& svg': {
        width: '24px !important',
        height: '24px !important',
      },
    },
    customRadioClass: {
      paddingTop: spacing(0.5),
      paddingLeft: spacing(0.5),
      height: '100%',
      width: 'inherit',
      alignItems: 'flex-start',
    },
    notificationCard: {
      padding: spacing(9.5, 14, 9.5, 14),
      marginTop: spacing(4),
    },
    linkWrapper: {
      marginTop: spacing(4),
    },
  })
);

const JobPostingDetailsPage = ({
  auth,
  accessControl,
  navigate,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.JOB_POSTING_DETAILS);

  const { jobId } = useParams();
  const { job } = useSelector(jobDetailsSelector);
  const { departments } = useSelector(divisionsAndDepartmentsSelector);
  const { categories } = useSelector(tagsSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditJobDrawerOpened, setIsEditJobDrawerOpened] = useState(false);

  const canEdit = hasAuthPermissionCheck(
    PERMISSION_CODES.RECRUITMENT_EDIT,
    accessControl
  );

  const hasSteps =
    job?.steps && isArray(job?.steps) ? !isArrayEmpty(job?.steps) : false;

  const getInitialData = useCallback(async () => {
    await Promise.all([
      getJob(dispatch, jobId),
      getDivisionsAndDepartments(dispatch),
      getTagsCategories(dispatch),
    ]);
    setIsLoading(false);
  }, [dispatch, jobId]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const cleanup = useCallback(() => {
    dispatch(clearJob());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearTagsCategories());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const totalCandidates = useMemo(() => sumCandidates(job?.steps), [job]);

  const activeCandidatesNumber = useMemo(
    () =>
      (Number(job?.number_of_active_candidates) || 0) -
      (Number(job?.number_of_hired_candidates) || 0),
    [job]
  );

  const handleEditJob = useCallback(() => {
    setIsEditJobDrawerOpened(true);
  }, []);

  const handleCloseEditJobDrawer = useCallback(() => {
    setIsEditJobDrawerOpened(false);
  }, []);

  const handleSaveJob = useCallback(
    async data => {
      await http.patch(api_job(jobId), data);
      showSuccessMessage(translations.successMessages.jobUpdated);
      setIsEditJobDrawerOpened(false);
      await getJob(dispatch, jobId);
    },
    [dispatch, jobId, translations.successMessages.jobUpdated]
  );

  const handleViewPipeline = useCallback(() => {
    navigate(`/jobs/${jobId}/details/0/profile/?stats=qualified`);
  }, [jobId, navigate]);

  const menuItems = useMemo(
    () =>
      getMenuItems(translations, canEdit, handleEditJob, handleViewPipeline),
    [translations, canEdit, handleEditJob, handleViewPipeline]
  );

  const renderTitle = () => {
    return (
      <div className={classes.headerWrapper}>
        <TextBoxWithTooltip text={job?.title} variant="h1" />
      </div>
    );
  };

  const formatJobDate = value =>
    value ? formatDate(value, DATE_FORMAT) : translations.summary.noValue;

  const renderField = (label, value, index) => {
    if (label === translations.summary.labels.createdBy) {
      return value ? (
        <div key={`job_item_${index}`} className={classes.sectionWrapper}>
          <Typography
            variant="body2"
            className={classNames(classes.label, classes.labelSpacing)}
          >
            {label}
          </Typography>
          <UserAvatar variant="subtitle2" user={value} extraSmall caption />
        </div>
      ) : (
        <Typography variant="subtitle2">
          {translations.summary.noValue}
        </Typography>
      );
    }
    if (isArray(value)) {
      return (
        <div key={`job_item_${index}`} className={classes.sectionWrapper}>
          <Typography
            variant="body2"
            className={classNames(classes.label, classes.labelSpacing)}
          >
            {label}
          </Typography>
          {!isArrayEmpty(value) ? (
            <div className={classes.tagsWrapper}>
              {value.map(tag => {
                return (
                  <div key={`tag_${tag.id}`} className={classes.tagWrapper}>
                    <Tag
                      tag={tag}
                      isSelected={false}
                      isSelectable={false}
                      color={tag.category_color}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            '-'
          )}
        </div>
      );
    }
    return (
      <div key={`job_item_${index}`} className={classes.sectionWrapper}>
        <Typography
          variant="body2"
          className={classNames(classes.label, classes.labelSpacing)}
        >
          {label}
        </Typography>
        <TextBoxWithTooltip text={value} variant="subtitle2" />
      </div>
    );
  };

  const handleEnablePublicLink = async () => {
    await http
      .patch(api_job_public(jobId), {
        public_access_allowed: !job?.public_access_allowed,
      })
      .then(() => {
        getInitialData();
      });
  };

  const getJobStatus = useMemo(() => {
    const today = new Date();
    if (job.archived_at && new Date(job.archived_at) <= today)
      return translations.archived;
    if (new Date(job.date_to) <= today) return translations.ended;
    return translations.open;
  }, [job.archived_at, job.date_to, translations]);

  const renderJobLink = useMemo(() => {
    const subdomain = getSubdomain(window.location.hostname);

    if (subdomain && subdomain.length > 0) {
      return `${API_PROTOCOL}://${subdomain}.${API_DOMAIN}/job/${job.public_slug}`;
    }
    return '';
  }, [job.public_slug]);

  const handleCopyLink = useCallback(async () => {
    if (!renderJobLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(renderJobLink);
      showSuccessMessage(translations.linkCopied);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = renderJobLink;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSuccessMessage(translations.linkCopied);
    }
  }, [renderJobLink, translations.linkCopied]);

  const JOB_SUMMARY_DATA = [
    {
      [translations.summary.labels.title]: job.title,
      [translations.summary.labels.status]: getJobStatus,
      [translations.summary.labels.department]: job?.department?.name || '-',
      [translations.summary.labels.workspace]:
        Object.values(WORKSPACE_MAP).find(
          workspace => workspace.id === job?.workspace
        )?.label || translations.summary.noValue,
      [translations.summary.labels.location]: job.location || '-',
      [translations.summary.labels.dateRange]: `${formatJobDate(
        job.date_from
      )} - ${formatJobDate(job.date_to)}`,
      [translations.summary.labels.createdBy]: job.created_by,
      [translations.summary.labels.tags]: job.tags,
    },
  ];

  const JOB_METRICS_DATA = [
    {
      [translations.metrics.createdAt]: formatJobDate(job.created_at),
      [translations.metrics.totalCandidates]: totalCandidates,
      [translations.metrics.decisionPipeline]: activeCandidatesNumber,
      [translations.metrics.hired]: job?.number_of_hired_candidates,
      [translations.metrics.lastActivity]: formatJobDate(job?.last_activity),
    },
  ];

  return (
    <PageContainer
      {...rest}
      translations={translations}
      isLoading={isLoading}
      auth={auth}
      accessControl={accessControl}
      navigate={navigate}
      headerProps={{
        renderTitle,
      }}
      shouldPassProps={false}
    >
      {!isLoading && (
        <div className={classes.root}>
          <SectionTitle
            className={classes.heading}
            title={translations.details}
          />
          <div className={classes.summaryRoot}>
            <div className={classes.summaryContent}>
              <Typography className={classes.cardSubtitle} variant="h5">
                {translations.summary.subtitle}
              </Typography>
              <div className={classes.summaryFieldsGrid}>
                {JOB_SUMMARY_DATA.map(summary_value =>
                  Object.entries(summary_value).map(([label, value], index) =>
                    renderField(label, value, index)
                  )
                )}
              </div>
            </div>
            <div className={classes.summarySide}>
              <Typography className={classes.cardSubtitle} variant="h5">
                {translations.metrics.subtitle}
              </Typography>
              {JOB_METRICS_DATA.map(metrics_data =>
                Object.entries(metrics_data).map(([label, value], index) =>
                  renderField(label, value, index)
                )
              )}
            </div>
            <div className={classes.editWrapper}>
              <DotsMenu className={classes.dotsMenu} menuItems={menuItems} />
            </div>
          </div>
          <div>
            <div className={classes.publishingSubtitle}>
              <SectionTitle
                className={classes.subtitle}
                title={translations.sections.publishing}
                variant="h5"
              />
              {canEdit && (
                <CustomSwitch
                  className={classNames(classes.subtitle, classes.switch)}
                  isDisabled={!hasSteps}
                  disabledTooltipText={translations.disabledSwitchMessage}
                  isChecked={job?.public_access_allowed}
                  onChange={handleEnablePublicLink}
                />
              )}
            </div>
            {job?.public_access_allowed && renderJobLink ? (
              <div className={classes.linkWrapper}>
                <Typography
                  variant="body2"
                  className={classNames(
                    classes.label,
                    classes.labelSpacing,
                    classes.publicLink
                  )}
                >
                  {renderJobLink}
                </Typography>
                <div className={classes.linkButtonWrapper}>
                  <a
                    href={renderJobLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CustomButton
                      className={classes.publicButton}
                      type="withTextLightRounded"
                      onClick={() => {}}
                    >
                      {translations.preview}
                    </CustomButton>
                  </a>
                  <CustomButton
                    className={classes.publicButton}
                    type="withTextLightRounded"
                    onClick={handleCopyLink}
                  >
                    {translations.copyLink}
                  </CustomButton>
                </div>
              </div>
            ) : (
              <NotificationCard
                className={classes.notificationCard}
                title={translations.noPublicLinkTitle}
                content={translations.noPublicLinkContent}
              />
            )}
          </div>
          <SectionTitle
            className={classes.subtitle}
            title={translations.sections.description}
            variant="h5"
          />
          <div className={classes.notes}>
            {job?.description ? (
              <RichTextPreview text={job.description} />
            ) : (
              <Typography variant="body2" className={classes.label}>
                {translations.emptySection}
              </Typography>
            )}
          </div>

          <SectionTitle
            className={classes.subtitle}
            title={translations.sections.requirements}
            variant="h5"
          />
          <div className={classes.notes}>
            {job?.requirements ? (
              <RichTextPreview text={job.requirements} />
            ) : (
              <Typography variant="body2" className={classes.label}>
                {translations.emptySection}
              </Typography>
            )}
          </div>

          <SectionTitle
            className={classes.subtitle}
            title={translations.sections.benefits}
            variant="h5"
          />
          <div className={classes.notes}>
            {job?.benefits ? (
              <RichTextPreview text={job.benefits} />
            ) : (
              <Typography variant="body2" className={classes.label}>
                {translations.emptySection}
              </Typography>
            )}
          </div>
        </div>
      )}
      <CustomFormDrawer
        translations={translations.manageJobs}
        isOpened={isEditJobDrawerOpened}
        initialData={prepareInitialData({
          ...job,
          tags: getObjectToNumberArray(job?.tags || []),
        })}
        fields={getFields(translations, classes)}
        departments={departments}
        categories={categories}
        onClose={handleCloseEditJobDrawer}
        onSave={handleSaveJob}
        isInitialValid
        hideDelete
        hasCancelButton
      />
    </PageContainer>
  );
};

JobPostingDetailsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(JobPostingDetailsPage);
